import { useEffect, type ReactNode } from 'react'
import { cn } from '../lib/cn'

export type ModalProps = {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  className?: string
  /** Reverse-link to .pen frame_id (e.g. `FFh3A`) — used by e2e via `[data-frame-id="..."]`. */
  frameId?: string
  /**
   * 落到 fixed 根容器上的 data-testid。给 e2e 用 `getByTestId(frame_id)` 锚定;
   * Modal 关闭时整体不渲染 → testid 消失 → `toBeHidden()` 自动成立。
   *
   * 注:之前 dialog wrapper 用 `<div data-testid>` 包裹 Modal,导致 wrapper 无 box
   * 始终被判 `hidden` —— 用本 prop 直接落到实际 fixed dialog 上即可。
   */
  'data-testid'?: string
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  className,
  frameId,
  'data-testid': testId,
}: ModalProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid={testId}
      data-frame-id={frameId}
    >
      <div
        className={cn(
          'w-full max-w-md rounded-lg bg-white shadow-xl',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title ? (
          <header className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-lg font-semibold">{title}</h2>
          </header>
        ) : null}
        <div className="p-4">{children}</div>
        {footer ? <footer className="border-t border-gray-200 p-3">{footer}</footer> : null}
      </div>
    </div>
  )
}
